export const PRIVATE_MEDIA_DB_NAME = 'anjabom-private-media';
const STORE_NAME = 'visit-photos';
const DB_VERSION = 1;
export const MAX_VISIT_PHOTO_BYTES = 12 * 1024 * 1024;

export interface VisitPhotoRecord {
  id: string;
  blob: Blob;
  savedAt: string;
}

export function validateVisitPhoto(file: Pick<File, 'type' | 'size'>): string | null {
  if (!file.type.startsWith('image/')) return '사진 파일만 올릴 수 있어요.';
  if (file.size > MAX_VISIT_PHOTO_BYTES) return '사진은 12MB 이하로 골라 주세요.';
  return null;
}

export function createVisitPhotoId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `visit-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function openDatabase(): Promise<IDBDatabase> {
  if (typeof indexedDB === 'undefined') return Promise.reject(new Error('사진 저장소를 사용할 수 없어요.'));
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(PRIVATE_MEDIA_DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) request.result.createObjectStore(STORE_NAME, { keyPath: 'id' });
    };
    request.onsuccess = () => { request.result.onversionchange = () => request.result.close(); resolve(request.result); };
    request.onerror = () => reject(request.error ?? new Error('사진 저장소를 열지 못했어요.'));
    request.onblocked = () => reject(new Error('다른 탭이 사진 저장소를 사용하고 있어요.'));
  });
}

async function transact<T>(mode: IDBTransactionMode, action: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const database = await openDatabase();
  try {
    return await new Promise<T>((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, mode);
      const request = action(transaction.objectStore(STORE_NAME));
      transaction.oncomplete = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('사진을 처리하지 못했어요.'));
      transaction.onabort = () => reject(transaction.error ?? new Error('사진 저장이 중단됐어요.'));
    });
  } finally {
    database.close();
  }
}

export async function saveVisitPhoto(id: string, blob: Blob): Promise<void> {
  await transact('readwrite', (store) => store.put({ id, blob, savedAt: new Date().toISOString() } satisfies VisitPhotoRecord));
}

export async function loadVisitPhoto(id: string): Promise<VisitPhotoRecord | null> {
  return (await transact<VisitPhotoRecord | undefined>('readonly', (store) => store.get(id))) ?? null;
}

export async function deleteVisitPhoto(id: string): Promise<void> {
  await transact('readwrite', (store) => store.delete(id));
}
